/**
 * Обход списка целей одним маршрутом (список покупок, запрос владельца
 * 03.08.2026): порядок остановок оптимизируется жадным проходом + 2-opt по
 * матрице РЕАЛЬНЫХ граф-расстояний (planRoute, не геометрия — стеллаж между
 * полками обходится, а не «пролетается»). Финал можно зафиксировать
 * (finishId: «закончить у касс») — он не участвует в перестановках.
 */
import type { BuiltGraph, RouteProfile } from './graph'
import { planRoute, type RoutePlan, type RouteStart } from './route'
import { targetNodeIds } from './route-points'
import type { Venue } from './types'

export interface TourStop {
  readonly targetId: string
  readonly plan: RoutePlan
}

export interface TourPlan {
  /** Остановки в порядке обхода; недостижимые цели сюда не входят. */
  readonly stops: readonly TourStop[]
  /** Порядок ВСЕХ целей (включая недостижимые — их место в конце). */
  readonly order: readonly string[]
  /** Цели, до которых маршрут не построился (нет сети/геометрии). */
  readonly skipped: readonly string[]
  readonly distanceM: number
  readonly durationS: number
  readonly profile: RouteProfile
  readonly start: RouteStart
}

export interface TourOpts {
  /** Фиксированная последняя остановка («кассы») — вне оптимизации порядка. */
  readonly finishId?: string
}

/** Старт «от цели»: её точка доступа — следующая нога тура начинается тут. */
function startOfTarget(
  venue: Venue,
  graph: BuiltGraph,
  targetId: string,
  profile: RouteProfile,
): RouteStart | null {
  const ids = targetNodeIds(venue, targetId, profile)
  const nodeId = ids.find((id) => graph.nodes.has(id))
  const node = nodeId ? graph.nodes.get(nodeId) : undefined
  return node && nodeId ? { point: node.point, nodeId, source: 'entrance' } : null
}

/** Жадный обход от старта (строка 0 матрицы): всегда к ближайшей непосещённой. */
function nearestNeighborOrder(dist: readonly (readonly number[])[], count: number): number[] {
  const order: number[] = []
  const visited = new Set<number>()
  let at = 0 // строка 0 — старт; цели — строки/столбцы 1..count
  for (let step = 0; step < count; step++) {
    let best = -1
    let bestD = Number.POSITIVE_INFINITY
    for (let j = 1; j <= count; j++) {
      if (visited.has(j)) continue
      const d = dist[at]?.[j] ?? Number.POSITIVE_INFINITY
      if (d < bestD) {
        bestD = d
        best = j
      }
    }
    if (best === -1) break
    visited.add(best)
    order.push(best)
    at = best
  }
  // Недостижимые (все расстояния Infinity) — в конец, порядок ввода сохраняется.
  for (let j = 1; j <= count; j++) if (!visited.has(j)) order.push(j)
  return order
}

/** Длина пути старт → цели по порядку (path, без возврата). */
function pathLength(dist: readonly (readonly number[])[], order: readonly number[]): number {
  let total = 0
  let at = 0
  for (const next of order) {
    total += dist[at]?.[next] ?? Number.POSITIVE_INFINITY
    at = next
  }
  return total
}

/** 2-opt для открытого пути: разворот сегмента, пока есть улучшение. */
function twoOptImprove(dist: readonly (readonly number[])[], initial: readonly number[]): number[] {
  const order = [...initial]
  let improved = true
  while (improved) {
    improved = false
    for (let i = 0; i < order.length - 1; i++) {
      for (let j = i + 1; j < order.length; j++) {
        const candidate = [
          ...order.slice(0, i),
          ...order.slice(i, j + 1).reverse(),
          ...order.slice(j + 1),
        ]
        if (pathLength(dist, candidate) + 1e-9 < pathLength(dist, order)) {
          order.splice(0, order.length, ...candidate)
          improved = true
        }
      }
    }
  }
  return order
}

/** Матрица граф-расстояний: строка 0 — старт, строки 1..N — цели. */
function buildMatrix(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  targets: readonly string[],
  profile: RouteProfile,
): number[][] {
  const starts: (RouteStart | null)[] = [
    start,
    ...targets.map((id) => startOfTarget(venue, graph, id, profile)),
  ]
  return starts.map((from) =>
    from === null
      ? [0, ...targets.map(() => Number.POSITIVE_INFINITY)]
      : [
          0,
          ...targets.map((to) => {
            const plan = planRoute(venue, graph, from, to, profile)
            return plan ? plan.distanceM : Number.POSITIVE_INFINITY
          }),
        ],
  )
}

/** Итоговые плечи по выбранному порядку; не построившееся плечо — в skipped. */
function buildLegs(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  ordered: readonly string[],
  profile: RouteProfile,
): { stops: TourStop[]; skipped: string[] } {
  const stops: TourStop[] = []
  const skipped: string[] = []
  let from: RouteStart = start
  for (const targetId of ordered) {
    const plan = planRoute(venue, graph, from, targetId, profile)
    if (!plan) {
      skipped.push(targetId)
      continue
    }
    stops.push({ targetId, plan })
    from = startOfTarget(venue, graph, targetId, profile) ?? {
      point: plan.polyline[plan.polyline.length - 1] ?? from.point,
      nodeId: plan.nodeIds[plan.nodeIds.length - 1] ?? from.nodeId,
      source: 'entrance',
    }
  }
  return { stops, skipped }
}

/**
 * Тур по списку целей. null — только если список пуст (после дедупликации)
 * или у площадки нет старта; отдельная недостижимая цель тур не валит.
 */
export function planTour(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  targetIds: readonly string[],
  profile: RouteProfile = 'foot',
  opts: TourOpts = {},
): TourPlan | null {
  const unique = [...new Set(targetIds)]
  const finish =
    opts.finishId !== undefined && unique.includes(opts.finishId) ? opts.finishId : null
  const middle = finish === null ? unique : unique.filter((id) => id !== finish)
  if (unique.length === 0) return null

  const dist = buildMatrix(venue, graph, start, middle, profile)
  const nn = nearestNeighborOrder(dist, middle.length)
  const improved = twoOptImprove(dist, nn)
  const ordered = improved.map((i) => middle[i - 1] as string)
  if (finish !== null) ordered.push(finish)

  const { stops, skipped } = buildLegs(venue, graph, start, ordered, profile)
  if (stops.length === 0) return null
  const distanceM = stops.reduce((acc, s) => acc + s.plan.distanceM, 0)
  const durationS = stops.reduce((acc, s) => acc + s.plan.durationS, 0)
  return { stops, order: ordered, skipped, distanceM, durationS, profile, start }
}
